import { GetStaticProps, GetStaticPaths } from "next";
import { getPostAndMorePosts, getCategoriesFivePostWithSlug } from "../../../lib/api";
import BlogSinglePostView from "../../../components/v2/blog/blog-single-post-view";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../../next-i18next.config";
import { extractFaqSchemaFromHtml } from "../../../lib/faqSchema";
import "../../../styles/scss/blog.module.scss";
import "../../../styles/scss/customer.module.scss";
import "../../../styles/scss/single-blog-v3.module.scss";

const SLUG_TO_POST: Record<string, string> = {
  tools: "kubernetes-monitoring-tools",
};

type Props = {
  post: any;
  allCatPosts: any;
  uri: string;
};

export default function KubernetesMonitoring({ post, allCatPosts, uri }: Props) {
  return (
    <BlogSinglePostView post={post} allCatPosts={allCatPosts} uri={uri} />
  );
}

export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
  previewData,
  locale,
}) => {
  const currentLocale = locale ?? "en";
  const routeSlug = params?.slug as string;
  const postSlug = SLUG_TO_POST[routeSlug];

  if (!postSlug) {
    return { notFound: true };
  }

  const data = await getPostAndMorePosts(
    postSlug,
    preview,
    previewData,
    currentLocale
  );
  const allCatPosts = await getCategoriesFivePostWithSlug();
  const post = data?.post;

  if (!post?.slug) {
    return { notFound: true };
  }

  const uri = `/blog/kubernetes-monitoring/${routeSlug}/`;
  const faqSchema = extractFaqSchemaFromHtml(post?.content) || null;

  return {
    props: {
      preview,
      post,
      allCatPosts: allCatPosts ?? null,
      description:
        post?.seo?.metaDesc ||
        "Read how observability can scale your cloud infrastructure, best practices, how-tos guide and industry updates from experts.",
      pageTitle:
        post?.seo?.title ||
        "Observability News, and Best Practices and Guides",
      ogImage: post?.featuredImage?.node?.sourceUrl || null,
      uri,
      metaTitle:
        post?.seo?.title ||
        "Observability News, and Best Practices and Guides",
      metaDesc:
        post?.seo?.metaDesc ||
        "Read how observability can scale your cloud infrastructure, best practices, how-tos guide and industry updates from experts.",
      metaRobotsNoindex: post?.seo?.metaRobotsNoindex || "index",
      metaRobotsNofollow: post?.seo?.metaRobotsNofollow || "follow",
      faqSchema,
      ...(await serverSideTranslations(
        currentLocale,
        ["common", "header", "footer"],
        nextI18NextConfig
      )),
    },
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const specificSlugs = Object.keys(SLUG_TO_POST);

  const paths = specificSlugs.flatMap((slug) => {
    if (locales && locales.length) {
      return locales.map((locale) => ({
        params: { slug },
        locale,
      }));
    }
    return [{ params: { slug } }];
  });

  return {
    paths,
    fallback: false,
  };
};
